/**
 * Master variants (1.2.0): the workspace's list (GET for anyone signed in; writes need admin), against
 * /api/v1/master-variants (key: queryKeys.masterVariants()). Complete in step 0.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { MasterVariantBody, MasterVariantDTO, MasterVariantPatch } from '@prism/contracts';
import { api } from '../client';
import { queryKeys } from '../queryKeys';

const BASE = '/api/v1/master-variants';

/** The variants in their order (Programme first unless moved). Rarely changes: kept for a minute. */
export function useMasterVariants(enabled = true) {
  return useQuery({
    queryKey: queryKeys.masterVariants(),
    queryFn: () => api<{ items: MasterVariantDTO[] }>(BASE),
    enabled,
    staleTime: 60_000,
  });
}

/** Create, change, reorder and delete; each refreshes the list (and the assets whose masters show a variant's name). */
export function useMasterVariantMutations() {
  const qc = useQueryClient();
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: queryKeys.masterVariants() });
    void qc.invalidateQueries({ queryKey: ['masters'] });
  };
  return {
    /** 409 NAME_TAKEN or CODE_TAKEN. */
    create: useMutation({ mutationFn: (body: MasterVariantBody) => api<MasterVariantDTO>(BASE, { method: 'POST', json: body }), onSuccess: refresh }),
    update: useMutation({
      mutationFn: ({ id, body }: { id: string; body: MasterVariantPatch }) => api<MasterVariantDTO>(`${BASE}/${id}`, { method: 'PATCH', json: body }),
      onSuccess: refresh,
    }),
    /** Every variant's id, in the new order. */
    reorder: useMutation({ mutationFn: (ids: string[]) => api<{ items: MasterVariantDTO[] }>(`${BASE}/order`, { method: 'PUT', json: { ids } }), onSuccess: refresh }),
    /** 409 VARIANT_IN_USE while a master has it; 409 BUILTIN for Programme. */
    remove: useMutation({ mutationFn: (id: string) => api<{ deleted: true }>(`${BASE}/${id}`, { method: 'DELETE' }), onSuccess: refresh }),
  };
}
